import React, { FC, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import clsx from "clsx";
import * as Yup from "yup";
import { useFormik } from "formik";
import { PageLink, PageTitle } from "../../../_metronic/layout/core";
import { Breadcrumb } from "../../../_metronic/layout/components/header/Breadcrumb";
import { AlertWidget, ModalWidget } from "../../UI";
import ErrorMessage1 from "../../components/ErrorMessage1";
import ConstantsContext from "../../../store/Constants-context";
import { getCredential } from "../../../hooks/credential.controller";
import { createAPIKey, deleteAPIKey } from "../../../hooks/apikey.controller";
import * as Constants from "../../../constants";
import {
  extractApiSettings,
  hasApiKey,
  initialNewApiUser,
  passwordMeetsPolicy,
  PASSWORD_RULES,
} from "./apiSettings-hooks";

const newApiUserSchema = Yup.object().shape({
  username: Yup.string()
    .trim("Cannot include leading and trailing spaces")
    .required("API Username is required"),
  email: Yup.string()
    .trim("Cannot include leading and trailing spaces")
    .email("Invalid Email Id")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .test(
      "password-policy",
      "Password does not meet the requirements below.",
      (value) => passwordMeetsPolicy(value || "")
    ),
  confirm_password: Yup.string()
    .required("Confirm Password is required")
    .oneOf(
      [Yup.ref("password")],
      "Password and Confirm password doesn't match"
    ),
});

const ApiSettings: FC = () => {
  const intl = useIntl();
  const constantsCtx = useContext(ConstantsContext);
  const errorColor = constantsCtx.ERROR_MESSAGE_COLOR_RED;

  const [loader, setLoader] = useState(true);
  const [busy, setBusy] = useState(false);
  const [keyExists, setKeyExists] = useState(false);
  const [apiKeyId, setApiKeyId] = useState<string>("");
  const [alertMessage, setAlertMessage] = useState<string | boolean>("");
  // Set when the initial credential load fails — the page can't be used until
  // it succeeds, so we show an inline error + Retry instead of the buttons.
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // The generated credentials are shown once, inline — the admin must record
  // them before dismissing (the key can never be retrieved again).
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [generatedUser, setGeneratedUser] = useState<string>("");

  const toast = (msg: string) => setAlertMessage(msg);

  // Turn any thrown error into a message worth showing the user. Network
  // failures surface as "Failed to fetch" / "Load failed" — replace those with
  // something actionable; otherwise pass the server's message through.
  const friendlyError = (e: any, fallback: string): string => {
    const msg = (e && e.message) || "";
    if (!msg || /failed to fetch|networkerror|load failed/i.test(msg)) {
      return "Network error — please check your connection and try again.";
    }
    return msg || fallback;
  };

  const loadData = () => {
    setLoader(true);
    setLoadError(null);
    return getCredential()
      .then((credential) => {
        const apiSettings = extractApiSettings(credential);
        if (!apiSettings[0]) {
          // No LoopLearnAPI settings — the tenant can't configure a key here.
          toast("Something went wrong");
          setTimeout(() => {
            window.location.href = "/setup.html";
          }, 1500);
          return;
        }
        setApiKeyId((apiSettings[0].api_key_id as string) || "");
        setKeyExists(hasApiKey(apiSettings[0]));
      })
      .catch((e) =>
        setLoadError(friendlyError(e, "Unable to load API settings."))
      )
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: initialNewApiUser,
    validationSchema: newApiUserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setBusy(true);
      try {
        const response: any = await createAPIKey({
          name: values.username.trim(),
          email: values.email.trim(),
          password: values.password.trim(),
        });
        if (response?.userMessage === "ERROR") {
          toast(response.returnData || "Could not generate API key.");
          return;
        }
        const returnData = response?.returnData || {};
        if (returnData.API_KEY_ID) setApiKeyId(returnData.API_KEY_ID);
        // Capture the username from the form before it's reset, so it can be
        // shown alongside the key in the one-time inline reveal.
        setGeneratedUser(values.username.trim());
        setGeneratedKey(returnData.API_KEY || "");
        setShowGenerateForm(false);
        formik.resetForm();
        toast("API Key Generated");
      } catch (e: any) {
        toast(friendlyError(e, "Could not generate API key."));
      } finally {
        setBusy(false);
        setSubmitting(false);
      }
    },
  });

  const handleDelete = async () => {
    setShowDeleteModal(false);
    setBusy(true);
    try {
      const response: any = await deleteAPIKey(apiKeyId, {
        "customer-code": Constants.cCode,
      });
      if (response?.userMessage === "ERROR") {
        toast(response.returnData || "Could not delete API key.");
        return;
      }
      setKeyExists(false);
      setApiKeyId("");
      toast("API Key Deleted");
    } catch (e: any) {
      toast(friendlyError(e, "Could not delete API key."));
    } finally {
      setBusy(false);
    }
  };

  const openGenerateForm = () => {
    formik.resetForm();
    setShowPassword(false);
    setShowGenerateForm(true);
  };

  const cancelGenerateForm = () => {
    setShowGenerateForm(false);
    formik.resetForm();
  };

  // Dismiss the one-time credential reveal; from here on only Delete is offered.
  const dismissResult = () => {
    setGeneratedKey(null);
    setGeneratedUser("");
    setKeyExists(true);
  };

  const breadcrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({ id: "MENU.SETUP" }),
      path: "/setup.html",
      isSeparator: true,
      isActive: false,
    },
    {
      title: intl.formatMessage({ id: "MENU.APISETTINGS" }),
      path: "/api-settings",
      isSeparator: false,
      isActive: false,
    },
  ];

  const invalid = (field: keyof typeof initialNewApiUser) =>
    formik.touched[field] && formik.errors[field];

  /* ---------- inline "Generate API User" form ---------- */
  const renderGenerateForm = () => (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className="p-5 mt-2"
      style={{
        border: "1px dashed #bfdedb",
        borderRadius: 6,
        background: "#fafbfb",
        maxWidth: 520,
      }}
    >
      <div
        style={{
          fontSize: 12.5,
          color: "#7b848d",
          fontWeight: 700,
          marginBottom: 12,
        }}
      >
        New API user
      </div>

      <div className="mb-4">
        <label className="form-label required" htmlFor="api-username">
          API Username
        </label>
        <input
          id="api-username"
          type="text"
          placeholder="API Username"
          autoComplete="off"
          className={clsx("form-control", {
            "is-invalid": invalid("username"),
          })}
          {...formik.getFieldProps("username")}
        />
        {invalid("username") && (
          <ErrorMessage1
            color={errorColor}
            errorMessage={formik.errors.username as string}
          />
        )}
      </div>

      <div className="mb-4">
        <label className="form-label required" htmlFor="api-email">
          Email
        </label>
        <input
          id="api-email"
          type="text"
          placeholder="Email"
          autoComplete="off"
          className={clsx("form-control", { "is-invalid": invalid("email") })}
          {...formik.getFieldProps("email")}
        />
        {invalid("email") && (
          <ErrorMessage1
            color={errorColor}
            errorMessage={formik.errors.email as string}
          />
        )}
      </div>

      <div className="mb-4">
        <label className="form-label required" htmlFor="api-password">
          Password
        </label>
        <div className="position-relative">
          <input
            id="api-password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            autoComplete="new-password"
            className={clsx("form-control", {
              "is-invalid": invalid("password"),
            })}
            {...formik.getFieldProps("password")}
          />
          <span
            className="position-absolute top-50 end-0 translate-middle-y me-3 text-primary"
            style={{ cursor: "pointer" }}
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? "Hide" : "Show"}
          </span>
        </div>
        {invalid("password") && (
          <ErrorMessage1
            color={errorColor}
            errorMessage={formik.errors.password as string}
          />
        )}
        {/* Live Cognito password-policy checklist */}
        <ul className="list-unstyled mt-2 mb-0">
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(formik.values.password);
            return (
              <li
                key={rule.label}
                className="d-flex align-items-center"
                style={{ fontSize: 12 }}
              >
                <i
                  className={clsx(
                    "bi me-2",
                    met
                      ? "bi-check-circle-fill text-success"
                      : "bi-circle text-muted"
                  )}
                ></i>
                <span className={met ? "text-success" : "text-muted"}>
                  {rule.label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mb-5">
        <label className="form-label required" htmlFor="api-confirm-password">
          Confirm Password
        </label>
        <input
          id="api-confirm-password"
          type="password"
          placeholder="Confirm Password"
          autoComplete="new-password"
          className={clsx("form-control", {
            "is-invalid": invalid("confirm_password"),
          })}
          {...formik.getFieldProps("confirm_password")}
        />
        {invalid("confirm_password") && (
          <ErrorMessage1
            color={errorColor}
            errorMessage={formik.errors.confirm_password as string}
          />
        )}
      </div>

      <div className="d-flex gap-3">
        <button type="submit" className="btn btn-ll" disabled={busy}>
          {busy ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          className="btn btn-secondary cancelButton"
          onClick={cancelGenerateForm}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  /* ---------- inline one-time credential reveal ---------- */
  const renderResult = () => (
    <div
      className="p-5 mt-2"
      style={{
        border: "1px solid #bfdedb",
        borderRadius: 6,
        background: "#f3faf9",
        maxWidth: 620,
      }}
    >
      <div className="d-flex align-items-center mb-3">
        <i className="bi bi-check-circle-fill text-success fs-3 me-2"></i>
        <span className="fw-bolder" style={{ fontSize: 15 }}>
          API user created
        </span>
      </div>
      <p style={{ fontSize: 14 }}>
        Please record these details in a safe place. Once this message is closed
        you will no longer be able to retrieve your API Key.
      </p>

      <div className="mb-3">
        <div className="text-muted" style={{ fontSize: 12 }}>
          API Username
        </div>
        <div className="fw-bolder text-break">{generatedUser || "—"}</div>
      </div>
      <div className="mb-5">
        <div className="text-muted" style={{ fontSize: 12 }}>
          API Key
        </div>
        <div className="fw-bolder text-break">{generatedKey || "—"}</div>
      </div>

      <button type="button" className="btn btn-ll" onClick={dismissResult}>
        Done
      </button>
    </div>
  );

  return (
    <>
      <Breadcrumb
        pageBreadcrumbs={breadcrumbs}
        activePageName={"api_settings"}
      />
      <PageTitle activePageName={"api_settings"}></PageTitle>

      {alertMessage && <AlertWidget message={alertMessage} />}

      <div className="row gy-5 gx-xl-8">
        <div className="col-xxl-12">
          <div className="card card-custom">
            <div className="card-header">
              <div className="card-title d-flex flex-column align-items-start">
                <h3 className="fw-bolder m-0">API Settings</h3>
                <span
                  className="text-muted mt-1"
                  style={{ fontSize: 13, fontWeight: 400 }}
                >
                  Generate an API user to connect your integration, or delete
                  the existing one.
                </span>
              </div>
            </div>

            <div className="card-body">
              {loader ? (
                <div className="d-flex justify-content-center py-10">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-hidden="true"
                  ></span>
                </div>
              ) : loadError ? (
                <div
                  className="d-flex align-items-center bg-light-danger rounded p-4"
                  style={{ maxWidth: 620 }}
                >
                  <i className="bi bi-exclamation-circle-fill text-danger fs-3 me-3"></i>
                  <div className="flex-grow-1 me-3 fw-bold">{loadError}</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-ll"
                    onClick={loadData}
                  >
                    Retry
                  </button>
                </div>
              ) : generatedKey !== null ? (
                renderResult()
              ) : keyExists ? (
                <button
                  type="button"
                  className="btn btn-red"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={busy}
                >
                  Delete API User
                </button>
              ) : showGenerateForm ? (
                renderGenerateForm()
              ) : (
                <button
                  type="button"
                  className="btn btn-ll"
                  onClick={openGenerateForm}
                  disabled={busy}
                >
                  Generate API User
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <ModalWidget
        title="Delete API User?"
        modalBody={
          <p style={{ fontSize: 15 }}>
            This permanently deletes the API key. Any integration using it will
            stop working — this cannot be undone.
          </p>
        }
        closeModal={() => setShowDeleteModal(false)}
        showModal={showDeleteModal}
        closeBtnText="Cancel"
        confirmBtnText="Delete API User"
        confirmBtnAction={handleDelete}
      />
    </>
  );
};

export { ApiSettings };
